/* eslint react-hooks/exhaustive-deps: 0 */

import { Card, Col, Row, Empty } from "antd";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { authStateSelector } from "../../../redux/features/auth/authSlice";
import { useGetAllDeviceOfUserQuery, useGetDeviceStatusQuery } from "../../../redux/services/deviceSite";
import { CompWrapper } from "../../common/customScrolbar";
import { DeviceCard } from "./deviceCard";
import { socket } from "../../../redux/services/webSocket";
import { WebsocketKey } from "../../../config/enum";
import "./style.css";

const normalizeMacKey = (mac: any) => String(mac || "").replace(/[:-]/g, "").toUpperCase();

export const DeviceTest = (props: any) => {

  const {user} = useSelector(authStateSelector)
  
  // Include roleName and roleId to help backend identify platform admin users
  const deviceDataQuery = useGetAllDeviceOfUserQuery({
    userId: user?.userId,
    roleName: user?.roleName,
    roleId: user?.roleId
  }, {
    refetchOnMountOrArgChange: true,
    // Skip query if userId is not available
    skip: !user?.userId
  })
  
  const getDeviceStatus = useGetDeviceStatusQuery({
    userId: user?.userId,
    roleName: user?.roleName,
    roleId: user?.roleId
  }, {
    refetchOnMountOrArgChange: true, 
    pollingInterval: 5000,
    // Don't skip even if userId is not available - let the API handle it
    skip: false
  })
  
  // State to store device status from WebSocket
  const [wsDeviceStatus, setWsDeviceStatus] = useState<any[] | null>(null);

  // WebSocket listener for device status updates (filtered by site/zone/tenant)
  useEffect(() => {
    if (!user?.userId) {
      console.log('[Device Status Page] WebSocket listener skipped - no userId', {
        userId: user?.userId,
        timestamp: new Date().toISOString()
      });
      return;
    }

    // Listen to user-specific event: device-status-update-{userId}
    const userSpecificEventName = `${WebsocketKey.DEVICE_STATUS_UPDATE}-${user.userId}`;
    
    // Listen to general event: device-status-update
    const generalEventName = WebsocketKey.DEVICE_STATUS_UPDATE;

    console.log('[Device Status Page] Registering WebSocket listeners', {
      userId: user?.userId,
      userSpecificEventName,
      generalEventName,
      timestamp: new Date().toISOString()
    });

    const handleDeviceStatusUpdate = (wsData: any) => {
      console.log('[Device Status Page] WebSocket device status update received', {
        userId: user?.userId,
        eventName: userSpecificEventName,
        wsDataKeys: Object.keys(wsData || {}),
        hasResult: !!wsData?.result,
        hasDataResult: !!wsData?.data?.result,
        fullWsData: wsData, // Log full structure to see what we're actually receiving
        timestamp: new Date().toISOString()
      });

      // Backend sends device status in various structures - handle all patterns
      let statusArray: any[] = [];
      
      // Pattern 1: wsData.result (array)
      if (wsData?.result && Array.isArray(wsData.result)) {
        statusArray = wsData.result;
        console.log('[Device Status Page] Device status from wsData.result', {
          count: statusArray.length,
          deviceMacs: statusArray.map((d: any) => d?.deviceMac).filter(Boolean)
        });
      } 
      // Pattern 2: wsData.data.result (nested result)
      else if (wsData?.data?.result && Array.isArray(wsData.data.result)) {
        statusArray = wsData.data.result;
        console.log('[Device Status Page] Device status from wsData.data.result', {
          count: statusArray.length,
          deviceMacs: statusArray.map((d: any) => d?.deviceMac).filter(Boolean)
        });
      } 
      // Pattern 3: wsData.data (direct array - THIS IS THE ACTUAL STRUCTURE!)
      else if (wsData?.data && Array.isArray(wsData.data)) {
        statusArray = wsData.data;
        console.log('[Device Status Page] Device status from wsData.data (direct array)', {
          count: statusArray.length,
          deviceMacs: statusArray.map((d: any) => d?.deviceMac).filter(Boolean)
        });
      }
      // Pattern 4: wsData itself is an array
      else if (Array.isArray(wsData)) {
        statusArray = wsData;
        console.log('[Device Status Page] Device status from wsData (direct array)', {
          count: statusArray.length,
          deviceMacs: statusArray.map((d: any) => d?.deviceMac).filter(Boolean)
        });
      } 
      // Pattern 5: Check if wsData.data has a nested structure we haven't checked
      else if (wsData?.data && typeof wsData.data === 'object' && !Array.isArray(wsData.data)) {
        // Try to find array in nested structure
        const dataKeys = Object.keys(wsData.data);
        console.log('[Device Status Page] Checking nested data structure', {
          dataKeys,
          wsDataData: wsData.data,
          timestamp: new Date().toISOString()
        });
        
        // Check common nested patterns
        if (Array.isArray(wsData.data.deviceStatus)) {
          statusArray = wsData.data.deviceStatus;
        } else if (Array.isArray(wsData.data.status)) {
          statusArray = wsData.data.status;
        } else if (Array.isArray(wsData.data.devices)) {
          statusArray = wsData.data.devices;
        }
        
        if (statusArray.length > 0) {
          console.log('[Device Status Page] Device status from nested wsData.data structure', {
            count: statusArray.length,
            deviceMacs: statusArray.map((d: any) => d?.deviceMac).filter(Boolean)
          });
        }
      }
      
      // If still no data found, log warning
      if (statusArray.length === 0) {
        console.warn('[Device Status Page] WebSocket data structure unexpected - logging full structure', {
          wsData,
          wsDataType: typeof wsData,
          isArray: Array.isArray(wsData),
          hasData: !!wsData?.data,
          dataType: typeof wsData?.data,
          dataIsArray: Array.isArray(wsData?.data),
          stringified: JSON.stringify(wsData, null, 2),
          timestamp: new Date().toISOString()
        });
      }

      // Filter WebSocket data to only include devices belonging to user's site/zone/tenant
      // Get list of MAC addresses from deviceDataQuery (devices belonging to user's site/zone/tenant)
      const userDevices = deviceDataQuery?.data?.result?.deviceData || [];
      const userDeviceMacKeys = new Set(
        userDevices.map((device: any) => normalizeMacKey(device?.mac)).filter(Boolean)
      );
      
      console.log('[Device Status Page] Filtering WebSocket data', {
        totalStatusCount: statusArray.length,
        userDeviceCount: userDevices.length,
        userDeviceMacKeys: Array.from(userDeviceMacKeys),
        timestamp: new Date().toISOString()
      });
      
      if (statusArray.length > 0 && userDeviceMacKeys.size > 0) {
        // Filter to only devices that belong to user's site/zone/tenant
        const filteredStatus = statusArray.filter((deviceStatus: any) => {
          const key = normalizeMacKey(deviceStatus?.deviceMac);
          return key && userDeviceMacKeys.has(key);
        });
        
        console.log('[Device Status Page] WebSocket data filtered', {
          beforeFilter: statusArray.length,
          afterFilter: filteredStatus.length,
          filteredDeviceMacs: filteredStatus.map((d: any) => d?.deviceMac),
          timestamp: new Date().toISOString()
        });
        
        if (filteredStatus.length > 0) {
          setWsDeviceStatus(filteredStatus);
          console.log('[Device Status Page] WebSocket device status updated', {
            count: filteredStatus.length,
            deviceStatuses: filteredStatus.map((d: any) => ({
              deviceMac: d?.deviceMac,
              status: d?.status
            })),
            timestamp: new Date().toISOString()
          });
        }
      } else if (statusArray.length > 0 && userDeviceMacKeys.size === 0) {
        // If deviceDataQuery hasn't loaded yet, store all statuses (will be filtered when deviceDataQuery loads)
        console.log('[Device Status Page] Device data not loaded yet - storing all WebSocket statuses temporarily', {
          statusCount: statusArray.length,
          timestamp: new Date().toISOString()
        });
        setWsDeviceStatus(statusArray);
      }
    };

    // Register listeners
    socket.on(userSpecificEventName, handleDeviceStatusUpdate);
    socket.on(generalEventName, handleDeviceStatusUpdate);

    console.log('[Device Status Page] WebSocket listeners registered', {
      userSpecificEventName,
      generalEventName,
      timestamp: new Date().toISOString()
    });

    // Cleanup listeners on unmount
    return () => {
      console.log('[Device Status Page] Cleaning up WebSocket listeners', {
        userSpecificEventName,
        generalEventName,
        timestamp: new Date().toISOString()
      });
      socket.off(userSpecificEventName, handleDeviceStatusUpdate);
      socket.off(generalEventName, handleDeviceStatusUpdate);
    };
  }, [user?.userId, deviceDataQuery?.data?.result?.deviceData]);

  useEffect(()=>{
    // Only refetch if queries are successful, not on errors
    if (deviceDataQuery?.isSuccess) {
      deviceDataQuery?.refetch()
    }
    if (getDeviceStatus?.isSuccess) {
      getDeviceStatus?.refetch()
    }
  },[getDeviceStatus?.isSuccess,deviceDataQuery?.isSuccess])

  // Log errors and success for debugging platform admin issues
  useEffect(() => {
    if (deviceDataQuery?.isError) {
      console.error('[Device Status Page] Error fetching device data', {
        error: deviceDataQuery?.error,
        userId: user?.userId,
        roleName: user?.roleName,
        roleId: user?.roleId,
        timestamp: new Date().toISOString()
      });
    }
    if (getDeviceStatus?.isError) {
      console.error('[Device Status Page] Error fetching device status', {
        error: getDeviceStatus?.error,
        userId: user?.userId,
        roleName: user?.roleName,
        roleId: user?.roleId,
        timestamp: new Date().toISOString()
      });
    }
    if (deviceDataQuery?.isSuccess) {
      console.log('[Device Status Page] Device data fetched successfully', {
        deviceCount: deviceDataQuery?.data?.result?.deviceData?.length || 0,
        userId: user?.userId,
        timestamp: new Date().toISOString()
      });
    }
    if (getDeviceStatus?.isSuccess) {
      console.log('[Device Status Page] Device status fetched successfully', {
        statusCount: getDeviceStatus?.data?.result?.length || 0,
        userId: user?.userId,
        timestamp: new Date().toISOString()
      });
    }
  }, [deviceDataQuery?.isError, getDeviceStatus?.isError, deviceDataQuery?.isSuccess, getDeviceStatus?.isSuccess, user?.userId, user?.roleName, user?.roleId])

  // Filter API polling data by site/zone/tenant (same as WebSocket filtering)
  const apiDeviceStatusFiltered = useMemo(() => {
    const userDevices = deviceDataQuery?.data?.result?.deviceData || [];
    const userDeviceMacKeys = new Set(
      userDevices.map((device: any) => normalizeMacKey(device?.mac)).filter(Boolean)
    );
    const apiStatus = getDeviceStatus?.data?.result || [];
    
    console.log('[Device Status Page] Filtering API polling data', {
      apiStatusCount: apiStatus.length,
      userDeviceCount: userDevices.length,
      userDeviceMacKeys: Array.from(userDeviceMacKeys),
      timestamp: new Date().toISOString()
    });
    
    if (userDeviceMacKeys.size > 0 && apiStatus.length > 0) {
      const filtered = apiStatus.filter((deviceStatus: any) => {
        const key = normalizeMacKey(deviceStatus?.deviceMac);
        return key && userDeviceMacKeys.has(key);
      });
      
      console.log('[Device Status Page] API polling data filtered', {
        beforeFilter: apiStatus.length,
        afterFilter: filtered.length,
        filteredDeviceMacs: filtered.map((d: any) => d?.deviceMac),
        timestamp: new Date().toISOString()
      });
      
      return filtered;
    }
    
    console.log('[Device Status Page] API polling data not filtered (returning all)', {
      apiStatusCount: apiStatus.length,
      reason: userDeviceMacKeys.size === 0 ? 'No user devices loaded' : 'No API status data',
      timestamp: new Date().toISOString()
    });
    
    return apiStatus;
  }, [getDeviceStatus?.data?.result, deviceDataQuery?.data?.result?.deviceData]);

  // Per device: prefer polling API (authoritative with backend); WS can be partial (only readers that sent traffic).
  const mergedDeviceStatus = useMemo(() => {
    const wsByMac = new Map<string, any>((wsDeviceStatus || []).map((d: any) => [normalizeMacKey(d?.deviceMac), d]));
    const apiByMac = new Map<string, any>((apiDeviceStatusFiltered || []).map((d: any) => [normalizeMacKey(d?.deviceMac), d]));
    const macs = new Set<string>(Array.from(wsByMac.keys()).concat(Array.from(apiByMac.keys())));
    const merged: any[] = [];

    macs.forEach((mac) => {
      if (!mac) return;
      const ws = wsByMac.get(mac);
      const api = apiByMac.get(mac);
      if (api) merged.push(api);
      else if (ws) merged.push(ws);
    });

    return merged;
  }, [wsDeviceStatus, apiDeviceStatusFiltered]);
  
  // Log merged device status
  useEffect(() => {
    console.log('[Device Status Page] Merged device status', {
      wsDeviceStatusCount: wsDeviceStatus?.length || 0,
      apiDeviceStatusCount: apiDeviceStatusFiltered?.length || 0,
      mergedCount: mergedDeviceStatus.length,
      dataSource: apiDeviceStatusFiltered?.length ? 'API per device (preferred)' : (wsDeviceStatus?.length ? 'WebSocket' : 'None'),
      deviceStatuses: mergedDeviceStatus.map((d: any) => ({
        deviceMac: d?.deviceMac,
        status: d?.status
      })),
      timestamp: new Date().toISOString()
    });
  }, [wsDeviceStatus, apiDeviceStatusFiltered, mergedDeviceStatus]);

  return (
    <>
      <section className="listing-section">
        <Card className="uicard table card-body-device uiinner-card">
          <CompWrapper>
            {deviceDataQuery?.data?.result?.deviceData <= 0 ?
              <div className="noDataFound">
                <Row>
                  <Col span={24}>
                    <Empty />
                  </Col>
                </Row>
              </div>
              :
              <Row gutter={[15, 15]}>
                {deviceDataQuery?.data?.length !== 0
                  ? deviceDataQuery?.data?.result?.deviceData?.map((obj: any,id: any) => (
                    <Col span={8} key={id}>
                      <DeviceCard device={obj} statusdevice={mergedDeviceStatus} tenantData={deviceDataQuery?.data?.result} />
                    </Col>
                  ))
                  : null}
              </Row>}
          </CompWrapper>
        </Card>
      </section>
    </>
  );
};
